import React, { useRef, useState } from "react";
import { api } from "../api/client";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Loader2, Upload, X } from "lucide-react";

const ALLOWED = [
  "image/png",
  "image/jpeg",
  "image/jpg",
  "image/webp",
  "image/gif",
  "image/svg+xml",
];
const MAX_BYTES = 2 * 1024 * 1024; // 2MB

/**
 * Logo upload with a drag & drop zone. Uploads the image to Azure Blob
 * (public container) and returns the public URL via onChange.
 */
export default function LogoUpload({ value, botId, onChange }) {
  const inputRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState(null);
  const [dragOver, setDragOver] = useState(false);

  const handleFile = async (file) => {
    if (!file) return;
    setError(null);
    if (!ALLOWED.includes(file.type)) {
      setError("Formato non supportato. Usa PNG, JPG, WEBP, GIF o SVG.");
      return;
    }
    if (file.size > MAX_BYTES) {
      setError("Immagine troppo grande (max 2MB).");
      return;
    }
    if (!botId) {
      setError("Bot non disponibile, riprova.");
      return;
    }
    setUploading(true);
    try {
      const { uploadUrl, blobPath } = await api.uploads.generateUrl(
        file.name,
        file.type,
        botId,
        "image"
      );
      await api.uploads.uploadFile(file, uploadUrl);
      onChange(blobPath);
    } catch (err) {
      console.error("Logo upload failed:", err);
      setError(err.message || "Caricamento non riuscito.");
    } finally {
      setUploading(false);
    }
  };

  const onDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    handleFile(e.dataTransfer.files?.[0]);
  };

  return (
    <div className="space-y-3">
      {value ? (
        <div className="flex items-center gap-3 rounded-lg border p-3">
          <img
            src={value}
            alt="Logo"
            className="h-12 w-12 rounded object-contain bg-muted"
          />
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium">Logo caricato</p>
          </div>
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => inputRef.current?.click()}
            disabled={uploading}
          >
            {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Cambia"}
          </Button>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={() => onChange("")}
            disabled={uploading}
            aria-label="Rimuovi"
          >
            <X className="h-4 w-4" />
          </Button>
        </div>
      ) : (
        <div
          onClick={() => inputRef.current?.click()}
          onDragOver={(e) => {
            e.preventDefault();
            setDragOver(true);
          }}
          onDragLeave={() => setDragOver(false)}
          onDrop={onDrop}
          className={`flex flex-col items-center justify-center gap-2 rounded-lg border-2 border-dashed p-6 cursor-pointer transition-colors ${
            dragOver
              ? "border-primary bg-primary/5"
              : "border-muted-foreground/25 hover:border-muted-foreground/40"
          }`}
        >
          {uploading ? (
            <>
              <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
              <p className="text-sm text-muted-foreground">Caricamento…</p>
            </>
          ) : (
            <>
              <Upload className="h-6 w-6 text-muted-foreground" />
              <p className="text-sm text-muted-foreground">
                Trascina un'immagine qui, o{" "}
                <span className="text-primary font-medium">scegli un file</span>
              </p>
              <p className="text-xs text-muted-foreground">
                PNG, JPG, WEBP, SVG · max 2MB
              </p>
            </>
          )}
        </div>
      )}

      <input
        ref={inputRef}
        type="file"
        accept="image/*"
        className="hidden"
        onChange={(e) => handleFile(e.target.files?.[0])}
      />

      {error && <p className="text-sm text-red-600">{error}</p>}

      {/* URL fallback (only when no logo is set yet) */}
      {!value && (
        <div className="flex items-center gap-2">
          <span className="text-xs text-muted-foreground whitespace-nowrap">
            oppure URL:
          </span>
          <Input
            value={value || ""}
            onChange={(e) => onChange(e.target.value)}
            placeholder="https://.../logo.png"
            className="h-8 text-xs"
          />
        </div>
      )}
    </div>
  );
}
